import type { QueueBoardRoom } from "../types";
import { queueFont } from "../queueFont";

type CustomRoomPickerProps = {
  clinicName: string;
  rooms: QueueBoardRoom[];
  roomsLoading: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onDisplay: () => void;
};

export function CustomRoomPicker({
  clinicName,
  rooms,
  roomsLoading,
  selectedIds,
  onToggle,
  onDisplay,
}: CustomRoomPickerProps) {
  return (
    <div
      className={`${queueFont} box-border flex min-h-screen w-full flex-col items-center bg-white px-6 pb-20 pt-12`}
    >
      <div className="w-full max-w-[520px]">
        <div className={`${queueFont} mb-2 text-center text-[28px] font-extrabold text-[#0b3a66]`}>
          {clinicName || "Queue display"}
        </div>
        <p className="mb-7 text-center text-[15px] leading-normal text-[#334155]">
          Choose which rooms appear on this screen, then open the patient-facing view.
        </p>

        {roomsLoading ? (
          <p className={`${queueFont} py-8 text-center text-base text-[#334155]`}>Loading rooms…</p>
        ) : rooms.length === 0 ? (
          <p className="mb-6 text-center text-[15px] leading-[1.55] text-[#334155]">
            No rooms found for this clinic. Add rooms in Settings (Manage time → Rooms), then return here.
          </p>
        ) : (
          <div className="divide-y divide-[#cbd5e1] overflow-hidden rounded-xl border border-[#cbd5e1] bg-slate-50">
            {rooms.map((room) => {
              const checked = selectedIds.has(room.id);
              return (
                <label
                  key={room.id}
                  className={`flex cursor-pointer items-center gap-[14px] px-[18px] py-4 ${checked ? "bg-sky-100" : "bg-transparent"}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(room.id)}
                    className="h-5 w-5 accent-[#0b3a66]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-bold text-[#0b3a66]">{room.name}</div>
                    <div className="mt-0.5 text-sm text-[#334155]">{room.doctorName}</div>
                    <div className="mt-1 text-xs text-slate-400">Room no. {room.id}</div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={onDisplay}
          disabled={selectedIds.size === 0 || roomsLoading || rooms.length === 0}
          className={`${queueFont} mt-6 w-full cursor-pointer rounded-xl border-0 bg-[#0b3a66] px-5 py-4 text-[17px] font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-400`}
        >
          Show queue board
        </button>
      </div>
    </div>
  );
}
